/**
 * CreateWorkspacePage — post-signup workspace creation form.
 *
 * Shown when a new user has no workspaces (detected by ProtectedRoute).
 * Creates a workspace via the auth API and redirects to dashboard.
 *
 * Also checks for recoverable workspaces (deactivated within 30 days)
 * and surfaces a recovery prompt before the creation form.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useQuery } from '@tanstack/react-query';
import { RotateCcw, ArrowLeft } from 'lucide-react';
import { SplitAuthLayout } from '@/layouts/SplitAuthLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { createWorkspace, fetchRecoverableWorkspaces, recoverWorkspace } from '@/services/authApi';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { fetchWorkspaces } from '@/services/authApi';
import { normalizeWebsite } from '@/lib/domain';

export default function CreateWorkspacePage() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { setCurrentWorkspace, setWorkspaces } = useWorkspaceStore();
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [recoveringId, setRecoveringId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Show the canonical tenant key the backend will actually store, so pasting a
  // full URL doesn't silently become something else (#264).
  const normalizedWebsite = useMemo(() => normalizeWebsite(website), [website]);

  const { data: recoverable = [] } = useQuery({
    queryKey: ['recoverable-workspaces'],
    queryFn: fetchRecoverableWorkspaces,
    staleTime: 60_000,
  });

  // Auto-generate slug preview from name
  const slugPreview = useMemo(
    () =>
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, ''),
    [name]
  );

  const handleRecover = async (workspaceId: string, workspaceName: string) => {
    setRecoveringId(workspaceId);
    try {
      await recoverWorkspace(workspaceId);
      const workspaces = await fetchWorkspaces();
      setWorkspaces(workspaces);
      const recovered = workspaces.find((w) => w.id === workspaceId);
      if (recovered) setCurrentWorkspace(recovered);
      toast.success(`Workspace "${workspaceName}" recovered successfully!`);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to recover workspace');
    } finally {
      setRecoveringId(null);
    }
  };

  // A logged-in user with no workspace is force-redirected here by
  // ProtectedRoute, so a plain history back would just loop straight back.
  // "Back" signs out and returns to login — the only non-looping escape.
  const handleBack = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const workspace = await createWorkspace({
        name,
        website: normalizeWebsite(website) || undefined,
      });
      setCurrentWorkspace(workspace);
      setWorkspaces([workspace]);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      const message =
        err?.response?.data?.detail || err?.message || 'Failed to create workspace';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SplitAuthLayout>
      <div className="space-y-6">
        <button
          type="button"
          onClick={handleBack}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-ink"
        >
          <ArrowLeft className="size-4" />
          Back to login
        </button>

        <div className="text-center">
          <h2 className="text-2xl font-semibold tracking-tight">Create your workspace</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Set up a workspace to start managing your SEO content
          </p>
        </div>

        {/* Recovery prompt */}
        {recoverable.length > 0 && (
          <div className="rounded-lg border border-teal/30 bg-teal-light p-4 space-y-3">
            <div className="flex items-center gap-2">
              <RotateCcw className="size-4 text-teal-dark shrink-0" />
              <p className="text-sm font-medium text-teal-dark">
                You have {recoverable.length === 1 ? 'a workspace' : 'workspaces'} that can be recovered
              </p>
            </div>
            <div className="space-y-2">
              {recoverable.map((ws) => (
                <div
                  key={ws.id}
                  className="flex items-center justify-between rounded-md bg-card border px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-medium">{ws.name}</p>
                    {ws.website && (
                      <p className="text-xs text-muted-foreground">{ws.website}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-teal-light text-teal-dark border-teal/20">
                      Recoverable
                    </Badge>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-teal/30 text-teal-dark hover:bg-teal-light"
                      disabled={recoveringId === ws.id}
                      onClick={() => handleRecover(ws.id, ws.name)}
                    >
                      {recoveringId === ws.id ? 'Recovering...' : 'Recover'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Recovery is available within 30 days of deactivation.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {recoverable.length > 0 && (
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">or create a new one</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ws-name">Workspace name</Label>
            <Input
              id="ws-name"
              type="text"
              placeholder="My Agency"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              maxLength={100}
            />
            {slugPreview && (
              <p className="text-xs text-muted-foreground">
                Slug: <code className="rounded bg-muted px-1 py-0.5">{slugPreview}</code>
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="ws-website">Website (optional)</Label>
            <Input
              id="ws-website"
              type="text"
              placeholder="example.com"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {normalizedWebsite && normalizedWebsite !== website.trim()
                ? `Will connect to ${normalizedWebsite}`
                : 'Connect a website to start generating content for it'}
            </p>
          </div>
          <Button type="submit" className="w-full bg-coral hover:bg-coral-hover" disabled={isLoading || !name.trim()}>
            {isLoading ? 'Creating...' : 'Create workspace'}
          </Button>
        </form>
      </div>
    </SplitAuthLayout>
  );
}
